import React, { useCallback, useEffect, useState } from 'react';
import { ProfileTemplateService, ProfileTemplate } from '@/services/profileTemplateService';
import { TemplateCard } from './TemplateCard';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import type { ProfileData } from '@/apps/loan-officer-profile/hooks/useProfileData';

const log = createComponentLogger('WebsiteTemplateGallery', 'templates');

interface Props {
  selectedTemplate: string | null;
  onSelectTemplate: (templateId: string) => void;
  userId: string;
  previewProfile: Partial<ProfileData>;
  selectionDisabled?: boolean;
  selectionDescriptionId?: string;
}

export const WebsiteTemplateGallery: React.FC<Props> = ({
  selectedTemplate,
  onSelectTemplate,
  userId,
  previewProfile,
  selectionDisabled = false,
  selectionDescriptionId
}) => {
  const [templates, setTemplates] = useState<ProfileTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewTemplate, setPreviewTemplate] = useState<ProfileTemplate | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await ProfileTemplateService.getActiveTemplates();
      // Filter out 'none' and 'custom' options - we handle those differently
      const filteredTemplates = data.filter(
        t => t.id !== 'none' && t.id !== 'custom'
      );
      setTemplates(filteredTemplates);
      log.info('Templates loaded', { metadata: { count: filteredTemplates.length } });
    } catch (error) {
      log.error('Failed to load templates', { userId }, error as Error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="website-template-gallery">
      <p className="website-template-gallery__intro">
        Pick a template, then generate the public URL.
      </p>
      
      <div className="website-template-gallery__grid" aria-label="Website templates">
        {/* Template Cards */}
        {templates.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate === template.id}
            onSelect={() => {
              log.info('Template selected in gallery', {
                metadata: {
                  templateId: template.id,
                  previousSelection: selectedTemplate
                }
              });
              onSelectTemplate(template.id);
            }}
            onPreview={() => setPreviewTemplate(template)}
            selectionDisabled={selectionDisabled}
            selectionDescriptionId={selectionDescriptionId}
          />
        ))}
      </div>

      {/* No Website Option */}
      {selectedTemplate === 'none' && (
        <div className="website-template-gallery__none">
          <p className="text-sm text-muted-foreground">
            No website will be created. You can always add one later.
          </p>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          previewProfile={previewProfile}
          onClose={() => setPreviewTemplate(null)}
          onSelect={() => {
            if (selectionDisabled) {
              return;
            }
            onSelectTemplate(previewTemplate.id);
            setPreviewTemplate(null);
            toast.success(`Selected: ${previewTemplate.name}`);
          }}
          selectionDisabled={selectionDisabled}
          selectionDescriptionId={selectionDescriptionId}
        />
      )}
    </div>
  );
};
